import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import { LedgerEntry } from '@dataTypes/billing.types'
import { logger } from '@utils/safeLogger'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_BILLING', '')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export type TransactionType = 'Billing' | 'Claim' | 'Procedure'

export type ClaimProcedure = {
  procedure_code: string
  procedure_date: string
  clinic_fee: number
  allowed_amount: number
  write_off: number
  estimated_insurance_pay: number | null
  estimated_patient_resp: number
  approved_amount: number
  deductible: number
  copay: number
}

export type ClaimEntry = {
  patientName: string
  payerCarrier: string
  subscriber: string
  provider: string
  entityType: string
  claimId: string
  claimStatus: string
  submittedAmount: number
  itemType: string
  dateSent: string
  checkNo: string
  checkStatus: string
  paidAmount: string
  modeOfPayment: string
  paymentDate: string
  procedures: ClaimProcedure[]
}

export type TransactionEntry = {
  date: string
  patient: string
  type: TransactionType
  description: string | null
  tooth_details: string | null
  charges: number | null
  amount: number | null
  insurance_adjustment: number | null
  patient_adjustment: number | null
  balance: number
  ledgerEntry?: LedgerEntry
  claimEntry?: ClaimEntry
}

/** sort: `date|patient|type` + `:ASC|:DESC` (e.g. `date:DESC`) — translated server-side to query orderBy. */
export type TransactionQueryFilters = {
  patient_id?: string | string[]
  clinic_id?: string | string[]
  transaction_type?: TransactionType | TransactionType[]
  /** Case-insensitive search on patient first/last name (backend query object). */
  search?: string
  sort?: string
  limit?: number
  offset?: number
}

export type GetTransactionEntriesResponse = ApiResponse<{
  entries?: TransactionEntry[]
  count?: number
}>

export const getTransactionEntries = async (
  filters?: TransactionQueryFilters,
): Promise<GetTransactionEntriesResponse> => {
  try {
    const response = await axios.get<GetTransactionEntriesResponse>(
      `${BUSINESS_LAYER_API}/api/v1/transaction`,
      {
        params: filters,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getTransactionEntries:', e)
    throw e
  }
}

export type ExportTransactionCsvFilters = Partial<TransactionQueryFilters>

export const exportTransactionEntriesCsv = async (
  filters?: ExportTransactionCsvFilters,
): Promise<Blob> => {
  const response = await axios.get<Blob>(
    `${BUSINESS_LAYER_API}/api/v1/transaction/export/csv`,
    { params: filters ?? {}, responseType: 'blob' },
  )
  return response.data
}
